/* eslint-disable no-param-reassign */
/* eslint-disable camelcase */
/* eslint-disable require-jsdoc */
import isEmpty from 'trtc-js-sdk-core/src/utils/is-empty';
import { IS_FIREFOX, IS_WIN, getChromeMajorVersion } from 'trtc-js-sdk-core/src/utils/environment';
import { NAME, REMOTE_STREAM_TYPE_AUX, REMOTE_STREAM_TYPE_MAIN } from 'trtc-js-sdk-core/src/common/constants';
import { isSelectedCandidatePair } from 'trtc-js-sdk-core/src/common/rtc-detection';
import { isArray, isNumber, isUndefined, performanceNow } from 'trtc-js-sdk-core/src/utils/utils';
import { AUDIO_LEVEL_SCALE, LocalScreenTrack, LocalVideoTrack, Logger, MediaType, MULTI_VIDEO_DATA_TYPE, VideoCodec } from 'trtc-js-sdk-core';
import DownlinkConnection from '../connection/downlink-connection';
import BadCaseDetector from 'trtc-js-sdk-core/src/metrics/bad-case-detector';
import { Room } from '../room';
import UplinkConnection from '../connection/uplink-connection';
import UplinkConnectionSPC from '../connection/single-peerconnection/uplink-connection-spc';
import DownlinkConnectionSPC from '../connection/single-peerconnection/downlink-connection-spc';
import innerEmitter from 'trtc-js-sdk-core/src/inner-emitter/inner-emitter';
import { INNER_EVENT } from 'trtc-js-sdk-core/src/inner-emitter/inner-type';
import { getAudioContext } from 'trtc-js-sdk-core/src/utils/audio-context';
import pressureDetector from 'trtc-js-sdk-core/src/metrics/pressure-detector';

export default class Stats {
  _log: Logger;
  _prevReportTime = 0;
  _prevReport: Partial<StatsReport> = {};
  _prevStats: Awaited<ReturnType<Stats['getStats']>> | null = null;
  _prevEncoderImplementation = '';
  _prevAuxEncoderImpl = '';
  _prevQualityLimitationReason = '';
  _prevAuxQualityLimitationReason = '';
  _prevDecoderImplementationMap = new Map();
  _decodeMap = new Map();
  _prevQpSum = 0;
  _prevAuxQpSum = 0;
  // 媒体总发送字节数
  totalBytesSent = 0;
  // 媒体总接收字节数
  totalBytesReceived = 0;
  private _spcStats: any[] | null = null;
  constructor(public room: Room, log: Logger) {
    this._log = log;
  }

  get statInterval() {
    if (this._prevReportTime === 0) {
      return 2;
    }
    return (Date.now() - this._prevReportTime) / 1000;
  }

  async getSenderStats(connection: UplinkConnection | UplinkConnectionSPC | null, room: Room) {
    const stats: SenderStat = {
      audio: { bytesSent: 0, packetsSent: 0, audioLevel: 0, totalAudioEnergy: 0 },
      video: {
        bytesSent: 0,
        packetsSent: 0,
        framesEncoded: 0,
        frameWidth: 0,
        frameHeight: 0,
        framesSent: 0,
        fpsCapture: 0
      },
      small: {
        bytesSent: 0,
        packetsSent: 0,
        framesEncoded: 0,
        frameWidth: 0,
        frameHeight: 0,
        framesSent: 0,
        fpsCapture: 0
      },
      auxiliary: {
        bytesSent: 0,
        packetsSent: 0,
        framesEncoded: 0,
        frameWidth: 0,
        frameHeight: 0,
        framesSent: 0,
        fpsCapture: 0
      },
      rtt: 0
    };
    const pc = connection?.getPeerConnection();
    if (connection && pc) {
      const ssrc = connection.getSSRC();
      try {
        const results = this._spcStats || await pc.getStats();
        results.forEach(item => {
          let videoType;
          let localVideoTrack;
          if (item.type === 'outbound-rtp') {
            const isVideo = (item.mediaType || item.kind) === NAME.VIDEO;
            if (isVideo) {
              if (item.ssrc === ssrc.video) {
                videoType = NAME.VIDEO;
                localVideoTrack = connection.localMainVideoTrack;
              } else if (item.ssrc === ssrc.small) {
                videoType = NAME.SMALL;
              } else if (item.ssrc === ssrc.auxiliary) {
                localVideoTrack = connection.localAuxVideoTrack;
                videoType = NAME.AUXILIARY;
              }
              if (!videoType) return;

              stats[videoType].bytesSent = item.bytesSent;
              stats[videoType].packetsSent = item.packetsSent;
              stats[videoType].framesEncoded = item.framesEncoded;
              if (!isUndefined(item.keyFramesEncoded)) stats[videoType].keyFramesEncoded = item.keyFramesEncoded;
              if (!isUndefined(item.nackCount)) stats[videoType].nackCount = item.nackCount;
              if (!isUndefined(item.pliCount)) stats[videoType].pliCount = item.pliCount;
              if (!isUndefined(item.retransmittedPacketsSent)) stats[videoType].retransmittedPacketsSent = item.retransmittedPacketsSent;
              if (!isUndefined(item.totalEncodeTime)) stats[videoType].totalEncodeTime = item.totalEncodeTime;
              if (!isUndefined(item.totalPacketSendDelay)) stats[videoType].totalPacketSendDelay = item.totalPacketSendDelay;

              let avgQp = 0;
              // 检测 QP 值，当 QP > 40 时打印日志
              // ref: https://www.w3.org/TR/webrtc-stats/#dom-rtcoutboundrtpstreamstats-qpsum
              if (!isUndefined(item.qpSum) && !isUndefined(item.framesEncoded) && item.framesEncoded > 0) {
                const currentQpSum = item.qpSum;
                const currentFramesEncoded = item.framesEncoded;
                const prevQpSum = videoType === NAME.VIDEO ? this._prevQpSum : this._prevAuxQpSum;
                const prevFramesEncoded = videoType === NAME.VIDEO
                  ? (connection.localMainVideoTrack?.stat?.framesEncoded || 0)
                  : (connection.localAuxVideoTrack?.stat?.framesEncoded || 0);

                // 计算平均 QP 值（只在有新编码帧时计算）
                if (currentFramesEncoded > prevFramesEncoded && currentQpSum > prevQpSum) {
                  const qpDiff = currentQpSum - prevQpSum;
                  const framesDiff = currentFramesEncoded - prevFramesEncoded;
                  avgQp = Math.round(qpDiff / framesDiff);

                  // const QP_HIGH_WARN_NUM = connection.videoCodec === VideoCodec.VP8 ? 80 : 35;
                  // const isVP
                  if (avgQp > 35 && connection.videoCodec === VideoCodec.H264) {
                    this._log.warn(`${videoType === NAME.AUXILIARY ? 'aux ' : ''}video encoder QP is high: ${avgQp}, ` +
                      `resolution: ${item.frameWidth}x${item.frameHeight}, ` +
                      `codec: ${connection.videoCodec}, `);
                  }
                }

                // 更新上次的 QP 值
                if (videoType === NAME.VIDEO) {
                  this._prevQpSum = currentQpSum;
                } else if (videoType === NAME.AUXILIARY) {
                  this._prevAuxQpSum = currentQpSum;
                }
              }

              // 检测编码器实现，可用于判断是否开启硬编
              // ref: https://w3c.github.io/webrtc-stats/#dom-rtcoutboundrtpstreamstats-encoderimplementation
              if (!isUndefined(item.encoderImplementation) && (
                (videoType === NAME.VIDEO && this._prevEncoderImplementation !== item.encoderImplementation) ||
                (videoType === NAME.AUXILIARY && this._prevAuxEncoderImpl !== item.encoderImplementation))) {
                let streamType = MULTI_VIDEO_DATA_TYPE.MULTI_DATA_BIG_IMG;
                let prevImplementation = this._prevEncoderImplementation;
                if (videoType === NAME.AUXILIARY) {
                  streamType = MULTI_VIDEO_DATA_TYPE.MULTI_DATA_AUX_IMG;
                  prevImplementation = this._prevAuxEncoderImpl;
                }
                innerEmitter.emit(INNER_EVENT.VIDEO_CODEC_IMPLEMENTATION_CHANGED, { userId: connection.userId, streamType, prevImplementation, implementation: item.encoderImplementation, codec: connection.videoCodec, isHWCodec: item.powerEfficientEncoder });
                this[videoType === NAME.VIDEO ? '_prevEncoderImplementation' : '_prevAuxEncoderImpl'] = item.encoderImplementation;
                localVideoTrack?.log.info(`encoderImplementation change to ${item.encoderImplementation}(${connection.videoCodec}) HWEncoder: ${item.powerEfficientEncoder}`);
              }

              if (item.ssrc === ssrc.video) {
                // 检测 qualityLimitationReason，在值变更时打印日志，有利于问题排查
                // ref: https://www.w3.org/TR/webrtc-stats/#dom-rtcqualitylimitationreason
                if (!isUndefined(item.qualityLimitationReason) && item.bytesSent !== 0 &&
                  this._prevQualityLimitationReason !== item.qualityLimitationReason
                ) {
                  localVideoTrack?.log.info(`qualityLimitationReason change to ${item.qualityLimitationReason}`);
                  innerEmitter.emit(INNER_EVENT.QUALITY_LIMITATION_CHANGED, {
                    userId: connection.userId,
                    reason: item.qualityLimitationReason,
                    prevReason: this._prevQualityLimitationReason,
                    streamType: MULTI_VIDEO_DATA_TYPE.MULTI_DATA_BIG_IMG,
                    isQosClearFirst: connection.localMainVideoTrack?.isQosClearFirst
                  });
                  this._prevQualityLimitationReason = item.qualityLimitationReason;
                }
              } else if (item.ssrc === ssrc.auxiliary &&
                !isUndefined(item.qualityLimitationReason) && item.bytesSent !== 0 &&
                this._prevAuxQualityLimitationReason !== item.qualityLimitationReason) {
                this._log.info(`aux qualityLimitationReason change to ${item.qualityLimitationReason}`);
                innerEmitter.emit(INNER_EVENT.QUALITY_LIMITATION_CHANGED, {
                  userId: connection.userId,
                  reason: item.qualityLimitationReason,
                  prevReason: this._prevAuxQualityLimitationReason,
                  streamType: MULTI_VIDEO_DATA_TYPE.MULTI_DATA_AUX_IMG,
                  isQosClearFirst: connection.localAuxVideoTrack?.isQosClearFirst
                });
                this._prevAuxQualityLimitationReason = item.qualityLimitationReason;
              }
            } else {
              stats.audio.bytesSent = item.bytesSent;
              stats.audio.packetsSent = item.packetsSent;
            }
          } else if (item.type === 'candidate-pair') {
            // Firefox 98+ 拿不到 rtt 数据，bug: https://bugzilla.mozilla.org/show_bug.cgi?id=1761334
            // 此处会有多个 candidate，只有 selected 的 rtt 值才是最准确，最及时的。
            // 非 selected 的 rtt 更新会存在很大的延迟（10s），如模拟弱网时，10s 才能看到 rtt 值有变更。
            if (isSelectedCandidatePair(item)) {
              this.totalBytesSent = item.bytesSent;
              if (isNumber(item.currentRoundTripTime)) {
                stats.rtt = Math.floor(item.currentRoundTripTime * 1000);
              }
            }
          } else if (item.type === 'media-source') {
            // chrome 78+ 'media-source'
            if (item.kind === NAME.AUDIO) {
              stats.audio.audioLevel = item.audioLevel || 0;
              stats.audio.totalAudioEnergy = item.totalAudioEnergy || 0;
              if (item.echoReturnLoss) {
                // console.warn(item.echoReturnLoss, item.echoReturnLossEnhancement);
              }
              // 优先使用 mediaStreamTrackAudioStats 接口统计的采集健康度(ms)，用于区分编码异常还是采集异常 ref: https://w3c.github.io/mediacapture-extensions/#the-mediastreamtrackaudiostats-interface
              if (!isUndefined(connection.localMainAudioTrack?.sourceTrack?.stats?.deliveredFramesDuration)) {
                stats.audio.totalSamplesDuration = connection.localMainAudioTrack.sourceTrack.stats.deliveredFramesDuration / 1000;
              } else if (item.totalSamplesDuration) {
                stats.audio.totalSamplesDuration = item.totalSamplesDuration;
              }
            } else if (item.kind === NAME.VIDEO) {
              if (item.trackIdentifier === connection.getVideoTrackId(NAME.VIDEO)) {
                // 自 v5.13 开始, 仪表盘上报的采集帧率由 推流 track 的帧率 改为原始采集 track 的帧率
                // Chrome 支持使用 mediaStreamTrackVideoStats 接口统计摄像头原始采集帧率. ref: https://w3c.github.io/mediacapture-extensions/#the-mediastreamtrackvideostats-interface
                if (connection.localMainVideoTrack?.sourceTrack?.stats?.deliveredFrames) {
                  const { deliveredFrames } = connection.localMainVideoTrack.sourceTrack.stats;
                  stats.video.framesCaptured = deliveredFrames;
                  // 1. 首次使用 framesPerSecond, 避免先采集一段时间再进房推流时上报一个很大的采集帧率
                  // 2. 切换摄像头后, deliveredFrames 会重新计算，此时使用 framesPerSecond 规避
                  if (connection.localMainVideoTrack.stat.framesCaptured && connection.localMainVideoTrack.stat.framesCaptured > 0 && deliveredFrames >= connection.localMainVideoTrack.stat.framesCaptured) {
                    stats.video.fpsCapture = Math.floor((deliveredFrames - connection.localMainVideoTrack.stat.framesCaptured) / this.statInterval);
                  } else {
                    stats.video.fpsCapture = item.framesPerSecond;
                  }
                } else {
                  stats.video.fpsCapture = item.framesPerSecond;
                }
              } else if (item.trackIdentifier === connection.getVideoTrackId(NAME.AUXILIARY)) {
                stats.auxiliary.fpsCapture = item.framesPerSecond;
              } else {
                stats.small.fpsCapture = item.framesPerSecond;
              }
            }
          }
          // w3c 标准改了获取 audioLevel 的位置 https://www.w3.org/TR/webrtc-stats/#dom-rtcmediastreamtrackstats-audiolevel
          if (!isUndefined(item.audioLevel) && connection.localMainAudioTrack?.mediaTrack && item.trackIdentifier === connection.localMainAudioTrack.mediaTrack.id) {
            stats.audio.audioLevel = item.audioLevel || 0;
          }
          if (!isUndefined(item.frameWidth)) {
            // this is the trackId for video sender
            let videoType: 'video' | 'auxiliary' | 'small' = NAME.SMALL;
            if (item.trackIdentifier === connection.getVideoTrackId(NAME.VIDEO) || item.ssrc === ssrc.video) {
              videoType = NAME.VIDEO;
            } else if (item.trackIdentifier === connection.getVideoTrackId(NAME.AUXILIARY) || item.ssrc === ssrc.auxiliary) {
              videoType = NAME.AUXILIARY;
            }
            stats[videoType].frameWidth = item.frameWidth;
            stats[videoType].frameHeight = item.frameHeight;
            stats[videoType].framesSent = item.framesSent;
          }
        });
        if (connection.localMainAudioTrack) {
          // 麦克风采集音量，用于对比上行发送音量。可以辅助排查经过 audioManager 处理后上行无声的 case。
          const localAudioTrackAudioLevel = connection.localMainAudioTrack.getInternalAudioLevel();
          const localAudioTrackAfter3AAudioLevel = connection.localMainAudioTrack.getInternalAudioLevelAfter3A();
          stats.audio.audioCaptureEnergyAfter3a = localAudioTrackAfter3AAudioLevel;
          stats.audio.micAudioLevel = localAudioTrackAudioLevel;
          // 可能浏览器 pc.getStats 不支持获取音量，则用 sdk 计算的兜底。
          if (stats.audio.audioLevel === 0) {
            stats.audio.audioLevel = localAudioTrackAfter3AAudioLevel ?? localAudioTrackAudioLevel;
          }
        }
      } catch (error) {
        this._log.warn(`failed to getStats on sender connection ${error}`);
      }
    }
    // 未推流时, 上报音频采集音量、音频采集健康度、视频采集帧率、屏幕分享采集帧率
    if (!connection?.localMainAudioTrack && room.capturedLocalMainAudioTrack) {
      const localMainAudioTrack = room.capturedLocalMainAudioTrack;
      if (localMainAudioTrack) {
        stats.audio.micAudioLevel = localMainAudioTrack.getInternalAudioLevel();
        stats.audio.audioCaptureEnergyAfter3a = localMainAudioTrack.getInternalAudioLevelAfter3A();
        if (!isUndefined(localMainAudioTrack.sourceTrack?.stats?.deliveredFramesDuration)) {
          stats.audio.totalSamplesDuration = localMainAudioTrack.sourceTrack.stats.deliveredFramesDuration / 1000;
        }
      }
    }
    if (!connection?.localMainVideoTrack) {
      this.updateVideoCaptureStats(room.capturedLocalMainVideoTrack, stats.video);
    }
    if (!connection?.localAuxVideoTrack) {
      const isAux = room.capturedLocalAuxVideoTrack?.mediaType === MediaType.AUX_VIDEO;
      this.updateVideoCaptureStats(room.capturedLocalAuxVideoTrack, stats[isAux ? 'auxiliary' : 'video']);
    }

    if (!this.totalBytesSent) {
      this.totalBytesSent += stats.audio.bytesSent + stats.video.bytesSent + stats.auxiliary.bytesSent;
    }

    (Object.keys(stats) as Array<keyof SenderStat>).forEach(key => {
      if (key === NAME.AUDIO) {
        if (room.capturedLocalMainAudioTrack) room.capturedLocalMainAudioTrack.stat = stats[key];
        if (connection?.localAuxAudioTrack) connection.localAuxAudioTrack.stat = stats[key];
      } else if (key === NAME.VIDEO) {
        // 屏幕分享走主流时，mediaType 为 BIG_VIDEO，SSRC 匹配到 video，但 track 在 capturedLocalAuxVideoTrack
        if (room.capturedLocalAuxVideoTrack?.mediaType === MediaType.BIG_VIDEO) {
          room.capturedLocalAuxVideoTrack.stat = stats[key];
        } else if (room.capturedLocalMainVideoTrack) {
          room.capturedLocalMainVideoTrack.stat = stats[key];
        }
      } else if (key === NAME.AUXILIARY) {
        // 屏幕分享走主流时，stats 已在 VIDEO 分支赋值，此处不再覆盖
        if (room.capturedLocalAuxVideoTrack && room.capturedLocalAuxVideoTrack.mediaType !== MediaType.BIG_VIDEO) {
          room.capturedLocalAuxVideoTrack.stat = stats[key];
        }
      }
    });

    // Firefox 不支持从 candidate 获取 rtt，此处用后台计算的 uplinkRTT 兜底。
    if (stats.rtt === 0) {
      stats.rtt = this.room.networkQuality?.uplinkRTT || 0;
    }
    return stats;
  }

  async getReceiverStats(connection: DownlinkConnection | DownlinkConnectionSPC) {
    const stats: ReceiverStat = {
      tinyId: connection.tinyId,
      userId: connection.userId,
      rtt: 0,
      hasAudio: false,
      hasVideo: false,
      hasAuxiliary: false,
      isSmallSubscribed: false,
      avSyncDelay: 0,
      audio: {
        bytesReceived: 0,
        packetsReceived: 0,
        packetsLost: 0,
        p2pDelay: 0,
        totalJitter: 0,
        totalJitterCount: 0,
        audioLevel: 0,
        totalAudioEnergy: 0,
        insertedSamplesForDeceleration: 0,
        removedSamplesForAcceleration: 0
      },
      video: {
        bytesReceived: 0,
        packetsReceived: 0,
        packetsLost: 0,
        framesReceived: 0,
        framesDecoded: 0,
        frameWidth: 0,
        frameHeight: 0,
        fpsDecoded: 0,
        freezeCount: 0,
        totalFreezesDuration: 0,
        totalJitter: 0,
        totalJitterCount: 0,
        p2pDelay: 0,
        codec: ''
      },
      auxiliary: {
        bytesReceived: 0,
        packetsReceived: 0,
        packetsLost: 0,
        framesReceived: 0,
        framesDecoded: 0,
        frameWidth: 0,
        frameHeight: 0,
        fpsDecoded: 0,
        totalJitter: 0,
        totalJitterCount: 0,
        p2pDelay: 0,
        codec: ''
      }
    };
    const pc = connection.getPeerConnection();
    if (pc) {
      try {
        const { ssrc } = connection;
        const { muteState, subscribeState } = connection;
        const results = this._spcStats || await pc.getStats();
        results.forEach(item => {
          if (item.type === 'codec') {
            this._decodeMap.set(item.id, item);
          }
          if (item.type === 'inbound-rtp') {
            const isAudio = (item.mediaType || item.kind) === NAME.AUDIO;
            if (isAudio) {
              if (item.ssrc !== ssrc.audio || !muteState.hasAudio) return;
              stats.audio.packetsReceived = item.packetsReceived;
              stats.audio.bytesReceived = item.bytesReceived;
              stats.audio.packetsLost = item.packetsLost;

              if (item.insertedSamplesForDeceleration) {
                stats.audio.insertedSamplesForDeceleration = item.insertedSamplesForDeceleration;
              }
              if (item.removedSamplesForAcceleration) {
                stats.audio.removedSamplesForAcceleration = item.removedSamplesForAcceleration;
              }
              if (item.totalSamplesDuration) {
                stats.audio.totalSamplesDuration = item.totalSamplesDuration;
              }
              if (item.totalSamplesReceived) {
                stats.audio.totalSamplesReceived = item.totalSamplesReceived;
              }
              if (item.concealedSamples) {
                stats.audio.concealedSamples = item.concealedSamples;
              }
              if (item.silentConcealedSamples) {
                stats.audio.silentConcealedSamples = item.silentConcealedSamples;
              }

              const { remoteAudioTrack } = connection;
              remoteAudioTrack.stat.packetsReceived = item.packetsReceived;
              remoteAudioTrack.stat.bytesReceived = item.bytesReceived;
              remoteAudioTrack.stat.packetsLost = item.packetsLost;
              stats.audio.p2pDelay = remoteAudioTrack.stat.end2EndDelay;

              stats.hasAudio = true;
            } else {
              // 过滤无效的下行 stat
              // ref: http://tapd.oa.com/TerWebG/bugtrace/bugs/view/1020396022082842079
              if (IS_FIREFOX && item.bytesReceived === 0) {
                return;
              }

              let remoteVideoTrack;
              // 主流视频数据
              if (item.ssrc === ssrc.video && muteState.hasVideo) {
                stats.video.packetsReceived = item.packetsReceived;
                stats.video.bytesReceived = item.bytesReceived;
                stats.video.packetsLost = item.packetsLost;
                stats.video.framesReceived = item.framesReceived;
                stats.video.framesDecoded = item.framesDecoded;
                // ref: https://www.w3.org/TR/webrtc-stats/#dom-rtcinboundrtpstreamstats-framespersecond
                stats.video.fpsDecoded = item.framesPerSecond;
                stats.hasVideo = true;
                connection.videoCodec = VideoCodec[this._decodeMap.get(item.codecId)?.mimeType.split('/')[1] as keyof typeof VideoCodec] || VideoCodec.H264;
                stats.video.codec = connection.videoCodec;
                remoteVideoTrack = connection.remoteVideoTrack;
                if (muteState.hasSmall && subscribeState.smallVideo) {
                  stats.isSmallSubscribed = true;
                }

                // 打印主流视频的解码器实现。
                if (
                  item.decoderImplementation &&
                  (!this._prevDecoderImplementationMap.has(stats.userId) ||
                    this._prevDecoderImplementationMap.get(stats.userId) !==
                    item.decoderImplementation)
                ) {
                  remoteVideoTrack.log.info(`decoderImplementation change to ${item.decoderImplementation}(${connection.videoCodec}) HWDecoder: ${item.powerEfficientDecoder}`);
                  innerEmitter.emit(INNER_EVENT.VIDEO_CODEC_IMPLEMENTATION_CHANGED, { userId: this.room.userId, remoteUserId: stats.userId, prevImplementation: this._prevDecoderImplementationMap.get(stats.userId), implementation: item.decoderImplementation, codec: connection.videoCodec, isHWCodec: item.powerEfficientDecoder });
                  this._prevDecoderImplementationMap.set(stats.userId, item.decoderImplementation);
                }

                if (!isUndefined(item.keyFramesDecoded)) {
                  remoteVideoTrack.updateKeyFramesDecoded(item.keyFramesDecoded);
                }
              }
              // 辅流的视频数据
              if (item.ssrc === ssrc.auxiliary && muteState.hasAuxiliary) {
                stats.auxiliary.packetsReceived = item.packetsReceived;
                stats.auxiliary.bytesReceived = item.bytesReceived;
                stats.auxiliary.packetsLost = item.packetsLost;
                stats.auxiliary.framesReceived = item.framesReceived;
                stats.auxiliary.framesDecoded = item.framesDecoded;
                // ref: https://www.w3.org/TR/webrtc-stats/#dom-rtcinboundrtpstreamstats-framespersecond
                stats.auxiliary.fpsDecoded = item.framesPerSecond;

                remoteVideoTrack = connection.remoteAuxiliaryTrack;
                stats.auxiliary.p2pDelay = remoteVideoTrack.stat.end2EndDelay;
                stats.hasAuxiliary = true;
                stats.video.codec = this._decodeMap.get(item.codecId)?.mimeType.split('/')[1].toLowerCase() || VideoCodec.H264;

                if (!isUndefined(item.keyFramesDecoded)) {
                  remoteVideoTrack.updateKeyFramesDecoded(item.keyFramesDecoded);
                }
              }
              if (remoteVideoTrack) {
                remoteVideoTrack.stat.packetsReceived = item.packetsReceived;
                remoteVideoTrack.stat.bytesReceived = item.bytesReceived;
                remoteVideoTrack.stat.packetsLost = item.packetsLost;
                // 触发解码插件后, 从 getStat 获取的 framesReceived, framesDecoded 可能是 0
                remoteVideoTrack.stat.framesReceived = item.framesReceived;
                remoteVideoTrack.stat.framesDecoded = item.framesDecoded;

                if (item.jitterBufferDelay) {
                  remoteVideoTrack.stat.jitterBufferDelay = Math.floor((item.jitterBufferDelay / item.jitterBufferEmittedCount) * 1000);
                }
                stats.video.p2pDelay = remoteVideoTrack.stat.end2EndDelay;
              }
            }

            if (item.jitterBufferDelay) {
              if (isAudio) {
                stats.audio.totalJitter = item.jitterBufferDelay;
                stats.audio.totalJitterCount = item.jitterBufferEmittedCount;
                stats.audio.estimatedPlayoutTimestamp = item.estimatedPlayoutTimestamp;
              } else {
                if (item.ssrc === ssrc.video && muteState.hasVideo) {
                  stats.video.totalJitter = item.jitterBufferDelay;
                  stats.video.totalJitterCount = item.jitterBufferEmittedCount;
                  stats.video.estimatedPlayoutTimestamp = item.estimatedPlayoutTimestamp;
                } else if (item.ssrc === ssrc.auxiliary && muteState.hasAuxiliary) {
                  stats.auxiliary.totalJitter = item.jitterBufferDelay;
                  stats.auxiliary.totalJitterCount = item.jitterBufferEmittedCount;
                }
              }
            }
          } else if (item.type === 'candidate-pair') {
            // 触发软解的 track 可能没有对应的 candidate pair
            // Firefox 98+ 拿不到 rtt 数据，bug: https://bugzilla.mozilla.org/show_bug.cgi?id=1761334
            // 此处会有多个 candidate，只有 selected 的 rtt 值才是最准确，最及时的。
            // 非 selected 的 rtt 更新会存在很大的延迟（10s），如模拟弱网时，10s 才能看到 rtt 值有变更。
            if (isSelectedCandidatePair(item)) {
              this.totalBytesReceived = item.bytesReceived;
              if (isNumber(item.currentRoundTripTime)) {
                stats.rtt = Math.floor(item.currentRoundTripTime * 1000);
              }
            }
          }
          // 统计分辨率
          // Firefox 不支持 track type，导致此处拿不到数据
          // W3C 标准将 frameWidth stat 从 item.type 'track' 迁移至 'inbound-rtp' 了，此处不能通过 item.type 来判断，应直接判断是否有 frameWidth 字段。
          // https://www.w3.org/TR/webrtc-stats/#ref-for-dom-rtcmediastreamtrackstats-framewidth-1
          // 主流视频数据
          if (!isUndefined(item.frameWidth)) {
            if (item.trackIdentifier === connection.getMainStreamVideoTrackId() || item.ssrc === ssrc.video) {
              // this is a track for video receiver
              stats.video.frameWidth = item.frameWidth;
              stats.video.frameHeight = item.frameHeight;
              connection.remoteVideoTrack.stat.frameWidth = item.frameWidth;
              connection.remoteVideoTrack.stat.frameHeight = item.frameHeight;
            }
            // 辅流的视频数据
            if (item.trackIdentifier === connection.getAuxStreamVideoTrackId() || item.ssrc === ssrc.auxiliary) {
              stats.auxiliary.frameWidth = item.frameWidth;
              stats.auxiliary.frameHeight = item.frameHeight;
              connection.remoteAuxiliaryTrack.stat.frameWidth = item.frameWidth;
              connection.remoteAuxiliaryTrack.stat.frameHeight = item.frameHeight;
            }
          }

          if (!isUndefined(item.audioLevel) && connection.muteState.audioAvailable && connection.remoteAudioTrack.mediaTrack && item.trackIdentifier === connection.remoteAudioTrack.mediaTrack.id) {
            stats.audio.audioLevel = item.audioLevel || 0;
            stats.audio.totalAudioEnergy = item.totalAudioEnergy || 0;
          }
        });
        if (stats.audio.audioLevel === 0 && connection.muteState.audioAvailable) {
          stats.audio.audioLevel = connection.remoteAudioTrack.getInternalAudioLevel() || 0;
        }
        if (!this.totalBytesReceived) {
          this.totalBytesReceived += stats.audio.bytesReceived + stats.video.bytesReceived + stats.auxiliary.bytesReceived;
        }
        if (!isUndefined(connection.remoteVideoTrack.player.stat?.fps)) {
          stats.video.fpsRender = connection.remoteVideoTrack.player.stat.fps;
        }
        if (!isUndefined(connection.remoteAuxiliaryTrack.player.stat?.fps)) {
          stats.auxiliary.fpsRender = connection.remoteAuxiliaryTrack.player.stat.fps;
        }
        const audioTs = stats.audio.estimatedPlayoutTimestamp;
        const videoTs = stats.video.estimatedPlayoutTimestamp;
        // 仅在远端同时发布音视频且音频可用时检测同步延迟
        if (audioTs && videoTs && connection.remoteAudioTrack.isAvailable && connection.remoteVideoTrack.isAvailable) {
          const avSyncDelay = videoTs - audioTs;
          // 差值过大（如 > 10000ms）说明数据可能异常，忽略 avSyncDelay 设置
          if (Math.abs(avSyncDelay) <= 10000) {
            stats.avSyncDelay = avSyncDelay;
          }
        }
      } catch (error) {
        this._log.warn(`failed to getStats on receiver connection ${error}`);
      }
    }

    if (stats.rtt === 0) {
      stats.rtt = this.room.networkQuality?.uplinkRTT || 0;
    }
    return stats;
  }

  async getStats(uplinkConnection: Room['uplinkConnection'], downlinkConnections: Room['remotePublishedUserMap']): Promise<{ senderStats: SenderStat | Record<string, never>; receiverStats: ReceiverStat[]; mediaPlayoutStats?: MediaPlayoutStats}> {
    let senderStats = {};
    let mediaPlayoutStats;
    const receiverStats: ReceiverStat[] = [];
    if (this.room.singlePC) {
      const peerConnection = this.room.singlePC.getPeerConnection();
      if (!peerConnection) return { senderStats, receiverStats };
      const startTime = performanceNow();
      const stats = await peerConnection.getStats();
      const endTime = performanceNow();
      if (endTime - startTime > 2000) {
        this._log.warn(`getStats cost ${endTime - startTime}ms`);
      }
      const filteredStats: any[] = [];
      const set = new Set(['inbound-rtp', 'outbound-rtp', 'track', 'candidate-pair', 'media-source', 'codec', 'media-playout']);
      stats.forEach(stat => set.has(stat.type) && filteredStats.push(stat));
      this._spcStats = filteredStats;
    }
    if (uplinkConnection || this.hasCapturedTracks()) {
      senderStats = await this.getSenderStats(uplinkConnection, this.room);
    }
    for (const [key, value] of downlinkConnections) {
      const st = await this.getReceiverStats(value);
      if (st) receiverStats.push(st);
    }
    if (downlinkConnections.size) mediaPlayoutStats = this.getMediaPlayoutStats(this._spcStats);
    return { senderStats, receiverStats, mediaPlayoutStats };
  }

  hasCapturedTracks() {
    return this.room.capturedLocalMainVideoTrack || this.room.capturedLocalMainAudioTrack || this.room.capturedLocalAuxVideoTrack;
  }

  // 获取两个数据的差值
  getDifferenceValue(prevValue?: number, newValue?: number): number {
    if (isEmpty(prevValue)) {
      return newValue as number;
    }
    // @ts-ignore
    const differenceValue = newValue - prevValue;
    if (differenceValue < 0) {
      return 0;
    }
    return differenceValue;
  }

  prepareReport({ stats, report, freezeMap, uplinkConnection }: { stats: Awaited<ReturnType<Stats['getStats']>>; report: StatsReport; freezeMap: any; uplinkConnection?: Room['uplinkConnection']; }) {
    // 主线程阻塞耗时(ms)累计值，后续做差值得到 2s 内的阻塞总耗时
    report.msg_device_info.uint32_task_cost = pressureDetector.totalBlockingDuration;

    // senders' stats
    if (!isEmpty(stats.senderStats)) {
      const msg_audio_status: UplinkAudioReport = {
        uint32_audio_level: stats.senderStats.audio.audioLevel * AUDIO_LEVEL_SCALE,
        uint32_audio_energy: (stats.senderStats.audio.totalAudioEnergy || 0) * 1000000,
        uint32_audio_codec_bitrate: stats.senderStats.audio.bytesSent
      };
      if (stats.senderStats.audio.micAudioLevel) {
        msg_audio_status.uint32_mic_audio_level = stats.senderStats.audio.micAudioLevel * AUDIO_LEVEL_SCALE;
      }
      if (!isUndefined(stats.senderStats.audio.audioCaptureEnergyAfter3a)) {
        msg_audio_status.uint32_audio_capture_energy_after3a = stats.senderStats.audio.audioCaptureEnergyAfter3a * AUDIO_LEVEL_SCALE;
      }
      if (stats.senderStats.audio.totalSamplesDuration) {
        report.msg_device_info.uint32_audio_capture_cost = stats.senderStats.audio.totalSamplesDuration;
      }
      const msg_video_status = [];
      if (stats.senderStats.video.bytesSent || stats.senderStats.video.fpsCapture) {
        const bigVideoStats: UplinkVideoReport = {
          uint32_video_stream_type: 2,
          uint32_video_codec_fps: stats.senderStats.video.framesSent,
          uint32_video_capture_fps: stats.senderStats.video.fpsCapture,
          uint32_video_width: stats.senderStats.video.frameWidth,
          uint32_video_height: stats.senderStats.video.frameHeight,
          uint32_video_codec_bitrate: stats.senderStats.video.bytesSent,
          uint32_video_enc_fps: stats.senderStats.video.framesEncoded,
          uint32_key_frame_count: stats.senderStats.video.keyFramesEncoded,
          uint32_nack_count: stats.senderStats.video.nackCount,
          uint32_pli_count: stats.senderStats.video.pliCount,
          uint32_encode_cost: (stats.senderStats.video.totalEncodeTime || 0) * 1000,
          uint32_send_packet_cost: (stats.senderStats.video.totalPacketSendDelay || 0) * 1000,
          uint32_video_arq_packets: stats.senderStats.video.retransmittedPacketsSent
        };
        msg_video_status.push(bigVideoStats);
      }
      if (stats.senderStats.small.bytesSent) {
        const smallVideoStats = {
          uint32_video_stream_type: 3,
          uint32_video_codec_fps: stats.senderStats.small.framesSent || 0,
          uint32_video_capture_fps: stats.senderStats.small.fpsCapture || 0,
          uint32_video_width: stats.senderStats.small.frameWidth || 0,
          uint32_video_height: stats.senderStats.small.frameHeight || 0,
          uint32_video_codec_bitrate: stats.senderStats.small.bytesSent,
          uint32_video_enc_fps: stats.senderStats.small.framesEncoded || 0,
          uint32_key_frame_count: stats.senderStats.small.keyFramesEncoded,
          uint32_nack_count: stats.senderStats.small.nackCount,
          uint32_pli_count: stats.senderStats.small.pliCount,
          uint32_encode_cost: (stats.senderStats.small.totalEncodeTime || 0) * 1000,
          uint32_send_packet_cost: (stats.senderStats.small.totalPacketSendDelay || 0) * 1000,
          uint32_video_arq_packets: stats.senderStats.small.retransmittedPacketsSent
        };
        msg_video_status.push(smallVideoStats);
      }
      if (stats.senderStats.auxiliary.bytesSent || stats.senderStats.auxiliary.fpsCapture) {
        const auxVideoStats = {
          uint32_video_stream_type: 7,
          uint32_video_codec_fps: stats.senderStats.auxiliary.framesSent || 0,
          uint32_video_capture_fps: stats.senderStats.auxiliary.fpsCapture || 0,
          uint32_video_width: stats.senderStats.auxiliary.frameWidth || 0,
          uint32_video_height: stats.senderStats.auxiliary.frameHeight || 0,
          uint32_video_codec_bitrate: stats.senderStats.auxiliary.bytesSent,
          uint32_video_enc_fps: stats.senderStats.auxiliary.framesEncoded || 0,
          uint32_key_frame_count: stats.senderStats.auxiliary.keyFramesEncoded,
          uint32_nack_count: stats.senderStats.auxiliary.nackCount,
          uint32_pli_count: stats.senderStats.auxiliary.pliCount,
          uint32_encode_cost: (stats.senderStats.auxiliary.totalEncodeTime || 0) * 1000,
          uint32_send_packet_cost: (stats.senderStats.auxiliary.totalPacketSendDelay || 0) * 1000,
          uint32_video_arq_packets: stats.senderStats.auxiliary.retransmittedPacketsSent
        };
        msg_video_status.push(auxVideoStats);
      }

      const msg_network_status = {
        uint32_bitrate: 0,
        uint32_lost: 0,
        uint32_rtt: stats.senderStats.rtt
      };

      report.msg_up_stream_info = {
        msg_audio_status,
        msg_video_status,
        msg_network_status
      };
    }
    const { statInterval } = this;

    report.msg_down_stream_info = [];
    stats.receiverStats.forEach(st => {
      const downStreamInfoItem: DownlinkReport = {
        msg_user_info: {
          str_identifier: st.userId,
          uint64_tinyid: st.tinyId
        },
        msg_network_status: {
          uint32_rtt: st.rtt,
          uint32_bitrate: 0,
          uint32_lost: 0
        },
        msg_audio_status: {},
        msg_video_status: []
      };

      if (st.hasAudio) {
        const msg_audio_status = {
          uint32_audio_p2p_delay: st.audio.p2pDelay,
          uint32_audio_cache_ms: st.audio.totalJitter,
          uint32_audio_cache_ms_count: st.audio.totalJitterCount,
          uint32_audio_codec_bitrate: st.audio.bytesReceived,
          uint32_audio_total_bitrate: st.audio.bytesReceived,
          uint32_audio_level: st.audio.audioLevel * 100000000,
          uint32_audio_energy: st.audio.totalAudioEnergy * 1000000,
          uint32_audio_receive: st.audio.packetsReceived,
          uint32_audio_origin_lost: st.audio.packetsLost
        };
        downStreamInfoItem.msg_audio_status = msg_audio_status;
      }

      if (st.hasVideo) {
        const freeze = freezeMap.get(`${st.userId}_${REMOTE_STREAM_TYPE_MAIN}`);
        const duration = freeze ? freeze.duration : 0;
        const main_video_stats = {
          uint32_video_stream_type: st.isSmallSubscribed ? 3 : 2,
          uint32_video_receive_fps: st.video.framesReceived,
          uint32_video_width: st.video.frameWidth,
          uint32_video_height: st.video.frameHeight,
          uint32_video_codec_bitrate: st.video.bytesReceived,
          uint32_video_receive: st.video.packetsReceived,
          uint32_video_origin_lost: st.video.packetsLost,
          uint32_video_block_time: duration,
          uint32_video_dec_fps: st.video.framesDecoded,
          uint32_video_codec_fps: st.video.fpsRender,
          uint32_video_cache_ms: st.video.totalJitter,
          uint32_video_cache_ms_count: st.video.totalJitterCount,
          uint32_video_p2p_delay: st.video.p2pDelay,
          uint32_video_codec: st.video.codec,
          int32_video_audio_relative_delay: st.avSyncDelay + 5000 // 后台不支持负数，在仪表盘展示时再 -5000 还原
        };
        downStreamInfoItem.msg_video_status.push(main_video_stats);
      }
      if (st.hasAuxiliary) {
        const freeze = freezeMap.get(`${st.userId}_${REMOTE_STREAM_TYPE_AUX}`);
        const duration = freeze ? freeze.duration : 0;
        const auxiliary_video_stats = {
          uint32_video_stream_type: 7,
          uint32_video_receive_fps: st.auxiliary.framesReceived,
          uint32_video_width: st.auxiliary.frameWidth,
          uint32_video_height: st.auxiliary.frameHeight,
          uint32_video_codec_bitrate: st.auxiliary.bytesReceived,
          uint32_video_receive: st.auxiliary.packetsReceived + st.auxiliary.packetsLost,
          uint32_video_origin_lost: st.auxiliary.packetsLost,
          uint32_video_block_time: duration,
          uint32_video_dec_fps: st.auxiliary.framesDecoded,
          uint32_video_codec_fps: st.video.fpsRender,
          uint32_video_cache_ms: st.auxiliary.totalJitter,
          uint32_video_cache_ms_count: st.auxiliary.totalJitterCount,
          uint32_video_p2p_delay: st.auxiliary.p2pDelay,
          uint32_video_codec: st.video.codec
        };
        downStreamInfoItem.msg_video_status.push(auxiliary_video_stats);
      }

      report.msg_down_stream_info.push(downStreamInfoItem);
    });

    if (stats.mediaPlayoutStats && !isEmpty(stats.mediaPlayoutStats)) {
      stats.mediaPlayoutStats.synthesizedSamplesDuration *= 1000;
      stats.mediaPlayoutStats.totalSamplesDuration *= 1000;
    }

    const prevReport = this._prevReport;
    const prevStats = this._prevStats;
    // it's fine that use JSON.parse/stringify to do a deep copy as
    // all field in the report is able to JSON stringify
    this._prevReport = JSON.parse(JSON.stringify(report));
    this._prevStats = JSON.parse(JSON.stringify(stats));


    if (report.msg_up_stream_info.msg_audio_status && prevReport.msg_up_stream_info!.msg_audio_status) {
      // 对上行音频做处理
      const prevUpStreamAudioObj = prevReport.msg_up_stream_info!.msg_audio_status;
      const upStreamAudioObj = report.msg_up_stream_info.msg_audio_status;

      // 之前没有数据，则将当前上报的数据设为0，避免 stopLocalAudio & startLocalAudio 场景出现很大的码率上报。
      if (prevUpStreamAudioObj.uint32_audio_codec_bitrate === 0) {
        upStreamAudioObj.uint32_audio_codec_bitrate = 0;
      } else {
        // 上行音频数据处理：uint32_audio_codec_bitrate （音频编码码率）
        const uint32_audio_codec_bitrate_difference = this.getDifferenceValue(
          prevUpStreamAudioObj.uint32_audio_codec_bitrate,
          upStreamAudioObj.uint32_audio_codec_bitrate
        );
        upStreamAudioObj.uint32_audio_codec_bitrate = Math.round((uint32_audio_codec_bitrate_difference * 8) / statInterval);
        report.msg_up_stream_info.msg_network_status.uint32_bitrate! += upStreamAudioObj.uint32_audio_codec_bitrate;
      }

      // 音频采集健康度 = 2s 内的采集样本量换算成 ms。
      if (prevReport.msg_device_info?.uint32_audio_capture_cost) {
        report.msg_device_info.uint32_audio_capture_cost = 2 * Math.floor(this.getDifferenceValue(prevReport.msg_device_info.uint32_audio_capture_cost, report.msg_device_info.uint32_audio_capture_cost) * 1000 / statInterval);
        if (report.msg_device_info.uint32_audio_capture_cost > 0) {
          uplinkConnection?.localMainAudioTrack?.updateAfter3aSilenceStartTime(stats.senderStats.audio.audioCaptureEnergyAfter3a ?? stats.senderStats.audio.micAudioLevel);
        }
      } else {
        // 第一次上报的数据可能不足 2s，不上报。
        delete report.msg_device_info.uint32_audio_capture_cost;
      }
    }

    // 主线程阻塞耗时差值计算：2s 内超过 100ms 阻塞任务总耗时(ms)
    if (!isUndefined(prevReport.msg_device_info?.uint32_task_cost) && !isUndefined(report.msg_device_info.uint32_task_cost)) {
      report.msg_device_info.uint32_task_cost = Math.round(this.getDifferenceValue(prevReport.msg_device_info.uint32_task_cost, report.msg_device_info.uint32_task_cost));
    }

    // 对上行视频数据做处理
    const prevUpStreamVideoList = prevReport.msg_up_stream_info!.msg_video_status;
    const upStreamVideoList = report.msg_up_stream_info.msg_video_status;

    upStreamVideoList.forEach(upStreamVideoItem => {
      const prevUpStreamVideoItem = prevUpStreamVideoList.find(item => item.uint32_video_stream_type === upStreamVideoItem.uint32_video_stream_type);
      // 之前没有数据，则将当前上报的数据设为0，避免 removeTrack addTrack 场景出现很大的码率上报。
      if (!prevUpStreamVideoItem || prevUpStreamVideoItem.uint32_video_codec_bitrate === 0) {
        upStreamVideoItem.uint32_video_codec_bitrate = 0;
        upStreamVideoItem.uint32_video_enc_fps = 0;
        upStreamVideoItem.uint32_video_codec_fps = 0;
        return;
      }
      let prev_uint32_video_codec_bitrate = 0;
      let prev_uint32_video_enc_fps = 0;
      let prev_uint32_video_codec_fps = 0;
      // 码率差值大于 0 时，才赋值 prev_uint32_video_codec_bitrate，避免出现：
      // 单 PC 更新 sdp 时，上行视频码率会突降，且 stats 累计的上行码率值会清 0，导致出现很大的码率上报。
      if (prevUpStreamVideoItem && upStreamVideoItem.uint32_video_codec_bitrate >= prevUpStreamVideoItem.uint32_video_codec_bitrate) {
        prev_uint32_video_codec_bitrate = prevUpStreamVideoItem.uint32_video_codec_bitrate;
        prev_uint32_video_enc_fps = prevUpStreamVideoItem.uint32_video_enc_fps;
        prev_uint32_video_codec_fps = prevUpStreamVideoItem.uint32_video_codec_fps;
      }

      // 上行视频数据处理：uint32_video_codec_bitrate（视频编码码率）
      const uint32_video_codec_bitrate_difference = this.getDifferenceValue(
        prev_uint32_video_codec_bitrate,
        upStreamVideoItem.uint32_video_codec_bitrate
      );
      upStreamVideoItem.uint32_video_codec_bitrate = Math.round((uint32_video_codec_bitrate_difference * 8) / statInterval);

      report.msg_up_stream_info.msg_network_status.uint32_bitrate! += upStreamVideoItem.uint32_video_codec_bitrate;

      // 上行视频数据处理：uint32_video_enc_fps（视频编码帧率）
      upStreamVideoItem.uint32_video_enc_fps = Math.round(this.getDifferenceValue(prev_uint32_video_enc_fps, upStreamVideoItem.uint32_video_enc_fps) /
        statInterval);

      // 上行视频数据处理：uint32_video_codec_fps (视频编码帧率)
      upStreamVideoItem.uint32_video_codec_fps = Math.round(this.getDifferenceValue(
        prev_uint32_video_codec_fps,
        upStreamVideoItem.uint32_video_codec_fps
      ) / statInterval);
      // 解决 Win Chrome 115 屏幕分享采集帧率掉 0 的 issue，导致上报仪表盘的视频发生帧率误报了一个很大值的问题。
      // https://bugs.chromium.org/p/chromium/issues/detail?id=1469201
      // 出现的原因：
      // stat1: frameSent = 100, frameWidth = 1920，frameHeight = 1080
      // stat2: frameSent = 0, frameWidth = 0, frameHeight = 0
      // stat3: frameSent = 115, frameWidth = 1920, frameHeight 1080  ====> 此时就会出现上报了一个很大的发送帧率，解决方法：此时将 uint32_video_codec_fps 用 uint32_video_enc_fps 上报替代。
      if (prevUpStreamVideoItem.uint32_video_width === 0 && prevUpStreamVideoItem.uint32_video_height === 0 && prevUpStreamVideoItem.uint32_video_codec_fps === 0) {
        upStreamVideoItem.uint32_video_codec_fps = upStreamVideoItem.uint32_video_enc_fps;
      }

      if (!isUndefined(prevUpStreamVideoItem.uint32_key_frame_count)) {
        upStreamVideoItem.uint32_key_frame_count = Math.round(this.getDifferenceValue(
          prevUpStreamVideoItem.uint32_key_frame_count,
          upStreamVideoItem.uint32_key_frame_count
        ));
      }
      if (!isUndefined(prevUpStreamVideoItem.uint32_nack_count)) {
        upStreamVideoItem.uint32_nack_count = Math.round(this.getDifferenceValue(
          prevUpStreamVideoItem.uint32_nack_count,
          upStreamVideoItem.uint32_nack_count
        ));
      }
      if (!isUndefined(prevUpStreamVideoItem.uint32_pli_count)) {
        upStreamVideoItem.uint32_pli_count = Math.round(this.getDifferenceValue(
          prevUpStreamVideoItem.uint32_pli_count,
          upStreamVideoItem.uint32_pli_count
        ));
      }
      if (!isUndefined(prevUpStreamVideoItem.uint32_video_arq_packets)) {
        upStreamVideoItem.uint32_video_arq_packets = Math.round(this.getDifferenceValue(
          prevUpStreamVideoItem.uint32_video_arq_packets,
          upStreamVideoItem.uint32_video_arq_packets
        ));
      }
      if (!isUndefined(prevUpStreamVideoItem.uint32_encode_cost)) {
        upStreamVideoItem.uint32_encode_cost = Math.round(this.getDifferenceValue(
          prevUpStreamVideoItem.uint32_encode_cost,
          upStreamVideoItem.uint32_encode_cost
        ) / statInterval);
      }
      if (!isUndefined(prevUpStreamVideoItem.uint32_send_packet_cost)) {
        upStreamVideoItem.uint32_send_packet_cost = Math.round(this.getDifferenceValue(
          prevUpStreamVideoItem.uint32_send_packet_cost,
          upStreamVideoItem.uint32_send_packet_cost
        ) / statInterval);
      }
    });

    // 处理下行上报数据
    const prevDownStreamInfo = prevReport.msg_down_stream_info;
    // 过滤掉之前没有数据的情况，避免出现：上报帧率、码率的累计值。
    report.msg_down_stream_info = report.msg_down_stream_info.filter(downStreamItemInfo => prevDownStreamInfo!.find(prevDownStreamItemInfo => (
      prevDownStreamItemInfo.msg_user_info.uint64_tinyid ===
      downStreamItemInfo.msg_user_info.uint64_tinyid
    )));
    const downStreamInfo = report.msg_down_stream_info;

    downStreamInfo.forEach(downStreamItemInfo => {
      const prevDownStreamItemInfo = prevDownStreamInfo!.find(prevDownStreamItemInfo => (
        prevDownStreamItemInfo.msg_user_info.uint64_tinyid === downStreamItemInfo.msg_user_info.uint64_tinyid
      ))!;
      if (!isEmpty(downStreamItemInfo.msg_audio_status) && !isEmpty(prevDownStreamItemInfo.msg_audio_status)) {
        // 对下行音频数据做处理
        const downStreamAudioInfo = downStreamItemInfo.msg_audio_status;
        const prevDownStreamAudioInfo = prevDownStreamItemInfo.msg_audio_status;

        // jitterBufferEmittedCount 用于辅助统计最近 2s 的 jitter buffer 大小，不直接上报。
        // @ts-ignore
        const jitterBufferEmittedCount = this.getDifferenceValue(prevDownStreamAudioInfo.uint32_audio_cache_ms_count, downStreamAudioInfo.uint32_audio_cache_ms_count);
        // @ts-ignore
        delete downStreamAudioInfo.uint32_audio_cache_ms_count;
        downStreamAudioInfo.uint32_audio_cache_ms = Math.floor(1000 * this.getDifferenceValue(prevDownStreamAudioInfo.uint32_audio_cache_ms, downStreamAudioInfo.uint32_audio_cache_ms) / jitterBufferEmittedCount) || 0;
        const downlinkConnection = this.room.remotePublishedUserMap.get(downStreamItemInfo.msg_user_info.str_identifier);
        if (downlinkConnection) {
          downlinkConnection.remoteAudioTrack.stat.jitterBufferDelay = downStreamAudioInfo.uint32_audio_cache_ms;
        }

        // 下行音频数据处理：uint32_audio_origin_lost (音频原始丢包数)
        downStreamAudioInfo.uint32_audio_origin_lost = this.getDifferenceValue(
          prevDownStreamAudioInfo.uint32_audio_origin_lost,
          downStreamAudioInfo.uint32_audio_origin_lost
        );

        // 下行音频数据处理：uint32_audio_receive (音频应收包数 = 音频收包数 + 音频原始丢包数)
        downStreamAudioInfo.uint32_audio_receive = this.getDifferenceValue(
          prevDownStreamAudioInfo.uint32_audio_receive,
          downStreamAudioInfo.uint32_audio_receive
        );
        downStreamAudioInfo.uint32_audio_receive += downStreamAudioInfo.uint32_audio_origin_lost;

        // 下行音频数据处理：uint32_audio_codec_bitrate (音频解码码率)
        const uint32_audio_codec_bitrate_difference = this.getDifferenceValue(
          prevDownStreamAudioInfo.uint32_audio_codec_bitrate,
          downStreamAudioInfo.uint32_audio_codec_bitrate
        );
        downStreamAudioInfo.uint32_audio_codec_bitrate = Math.round((uint32_audio_codec_bitrate_difference * 8) / statInterval);

        // 下行音频数据处理：uint32_audio_total_bitrate（音频码率包括原始包、FEC包、ARQ包）
        // 这里和 uint32_audio_codec_bitrate 是一致的
        downStreamAudioInfo.uint32_audio_total_bitrate = Math.round((uint32_audio_codec_bitrate_difference * 8) / statInterval);
      } else {
        // 远端关闭麦克风，重新打开麦克风时，为避免上报了很大的码率值，此处需上报空对象。
        downStreamItemInfo.msg_audio_status = {};
      }

      if (downStreamItemInfo.msg_video_status && prevDownStreamItemInfo.msg_video_status) {
        // 对下行视频数据做处理
        const prevDownStreamVideoList = prevDownStreamItemInfo.msg_video_status;
        // 之前没有数据，当前上报有数据，则剔除掉。
        downStreamItemInfo.msg_video_status = downStreamItemInfo.msg_video_status.filter(downStreamVideoItem => prevDownStreamVideoList.find(item => item.uint32_video_stream_type === downStreamVideoItem.uint32_video_stream_type));
        const downStreamVideoList = downStreamItemInfo.msg_video_status;
        downStreamVideoList.forEach(downStreamVideoItem => {
          // 匹配相同类型(主大流、主小流、辅流)的视频 stat
          const prevDownStreamVideoItem = prevDownStreamVideoList.find(item => item.uint32_video_stream_type === downStreamVideoItem.uint32_video_stream_type)!;
          const prev_uint32_video_receive = prevDownStreamVideoItem.uint32_video_receive;
          const prev_uint32_video_origin_lost = prevDownStreamVideoItem.uint32_video_origin_lost;
          const prev_uint32_video_codec_bitrate = prevDownStreamVideoItem.uint32_video_codec_bitrate;
          const prev_uint32_video_receive_fps = prevDownStreamVideoItem.uint32_video_receive_fps;
          const prev_uint32_video_dec_fps = prevDownStreamVideoItem.uint32_video_dec_fps;

          // 下行视频数据处理：uint32_video_origin_lost（视频原始丢包数）
          downStreamVideoItem.uint32_video_origin_lost = this.getDifferenceValue(
            prev_uint32_video_origin_lost,
            downStreamVideoItem.uint32_video_origin_lost
          );

          // 下行视频数据处理：uint32_video_receive（视频应收包数 = 视频收包数 + 视频原始丢包数）
          downStreamVideoItem.uint32_video_receive =
            this.getDifferenceValue(
              prev_uint32_video_receive,
              downStreamVideoItem.uint32_video_receive
            ) + downStreamVideoItem.uint32_video_origin_lost;

          // 下行视频数据处理：uint32_video_codec_bitrate (视频解码码率)
          const uint32_video_codec_bitrate_difference = this.getDifferenceValue(
            prev_uint32_video_codec_bitrate,
            downStreamVideoItem.uint32_video_codec_bitrate
          );
          downStreamVideoItem.uint32_video_codec_bitrate = Math.round((uint32_video_codec_bitrate_difference * 8) / statInterval);

          // 下行视频数据处理：uint32_video_receive_fps (视频接收帧率)
          const uint32_video_receive_fps_difference = this.getDifferenceValue(
            prev_uint32_video_receive_fps,
            downStreamVideoItem.uint32_video_receive_fps
          );
          downStreamVideoItem.uint32_video_receive_fps = Math.round(uint32_video_receive_fps_difference / statInterval);
          downStreamVideoItem.uint32_video_dec_fps = Math.round(this.getDifferenceValue(
            prev_uint32_video_dec_fps,
            downStreamVideoItem.uint32_video_dec_fps
          ) / statInterval);

          // jitterBufferEmittedCount 用于辅助统计最近 2s 的 jitter buffer 大小，不直接上报。
          // @ts-ignore
          const jitterBufferEmittedCount = this.getDifferenceValue(prevDownStreamVideoItem.uint32_video_cache_ms_count, downStreamVideoItem.uint32_video_cache_ms_count);
          // @ts-ignore
          delete downStreamVideoItem.uint32_video_cache_ms_count;
          downStreamVideoItem.uint32_video_cache_ms = Math.floor(1000 * this.getDifferenceValue(prevDownStreamVideoItem.uint32_video_cache_ms, downStreamVideoItem.uint32_video_cache_ms) / jitterBufferEmittedCount) || 0;
        });
      }
    });
    // 处理下行音频播放数据
    if (!isUndefined(prevStats?.mediaPlayoutStats?.totalSamplesDuration) && !isUndefined(stats.mediaPlayoutStats?.totalSamplesDuration)) {
      const synthesizedSamplesDuration = 2 * Math.floor(this.getDifferenceValue(prevStats?.mediaPlayoutStats?.synthesizedSamplesDuration, stats.mediaPlayoutStats?.synthesizedSamplesDuration) / statInterval);
      const totalSamplesDuration = 2 * Math.floor(this.getDifferenceValue(prevStats?.mediaPlayoutStats?.totalSamplesDuration, stats.mediaPlayoutStats?.totalSamplesDuration) / statInterval);
      report.msg_device_info.uint32_audio_play_cost = totalSamplesDuration - synthesizedSamplesDuration;
    }

    if (prevStats) {
      stats.receiverStats.forEach(receiverStat => {
        if (receiverStat.audio.concealedSamples && receiverStat.audio.totalSamplesReceived) {
          const prevReceiverStat = prevStats.receiverStats.find(item => item.userId === receiverStat.userId);
          if (prevReceiverStat && prevReceiverStat.audio.concealedSamples && prevReceiverStat.audio.totalSamplesReceived) {
            // 静音样本量，concealedSamples 的子集
            const silentConcealedSamplesIn2s = (receiverStat.audio.silentConcealedSamples || 0) - (prevReceiverStat.audio.silentConcealedSamples || 0);
            // 本地合成的假样本量
            const concealedSamplesIn2s = receiverStat.audio.concealedSamples - prevReceiverStat.audio.concealedSamples;
            const totalSamplesReceivedIn2s = receiverStat.audio.totalSamplesReceived - prevReceiverStat.audio.totalSamplesReceived;
            // 计算卡顿时，需要剔除静音的假样本。原因：native 在用户不说话时，eos 会降音频码率，此时静音样本量会增加。
            const blockTime = Math.floor((concealedSamplesIn2s - silentConcealedSamplesIn2s) / totalSamplesReceivedIn2s * 1000 * statInterval);
            // 假样本数量占比超过 1/5 则认为音频卡顿。
            if (blockTime > statInterval * 1000 / 5) {
              const downStat = downStreamInfo.find(item => item.msg_user_info.str_identifier === receiverStat.userId);
              if (downStat) {
                downStat.msg_audio_status.uint32_audio_block_time = blockTime;
              }
            }
          }
        }
      });
    }
    report.msg_down_stream_info.forEach(item => {
      item.msg_video_status.forEach(videoItem => {
        // 远端视频流已经停了，但是此时下行分辨率可能不为 0，检测这种情况重置为 0
        if (videoItem.uint32_video_codec_bitrate === 0 && videoItem.uint32_video_receive_fps === 0) {
          videoItem.uint32_video_width = 0;
          videoItem.uint32_video_height = 0;
        }
      });
    });
    return report;
  }

  async getStatsReport({ uplinkConnection, downlinkConnections, freezeMap }: { uplinkConnection: Room['uplinkConnection']; downlinkConnections: Room['remotePublishedUserMap']; freezeMap: ReturnType<BadCaseDetector['getMonitorFreeze']>; }) {
    const report: StatsReport = {
      msg_device_info: {},
      msg_up_stream_info: {
        msg_audio_status: {
          uint32_audio_format: 11, // 音频格式 11:opus 10:aac (UP/DOWN)
          uint32_audio_sample_rate: 0, // 音频采样率 单位:HZ
          uint32_audio_codec_bitrate: 0, // 音频编解码码率
          uint32_audio_receive: 0, // 音频应收包数 服务端自己计算
          uint32_audio_origin_lost: 0, // 音频原始丢包数 服务端自己计算或者0
          uint32_audio_level: 0,
          uint32_audio_energy: 0,
          uint32_audio_capture_energy_after3a: 0 // 3A 处理后的音量
        },
        msg_video_status: [
          // {
          //   uint32_video_stream_type: 0, // 视频流类型 2:大画面 3:小画面 7:辅路 (U/D)
          //   uint32_video_codec_fps: 0, // 仪表盘“发送帧率”
          //   uint32_video_capture_fps: 0, // 视频采集帧率
          //   uint32_video_width: 0, // 视频分辨率宽
          //   uint32_video_height: 0, // 视频分辨率高
          //   uint32_video_codec_bitrate: 0, // 视频编解码码率
          //   uint32_video_receive: 0, // 视频应收包数 服务端自己统计
          //   uint32_video_origin_lost: 0, // 视频原始丢包数 服务端自己统计
          //   uint32_video_final_lost: 0, // 视频最终丢包数 服务端自己统计
          //   uint32_video_enc_fps: 0 // 仪表盘“webrtc 编码帧率”
          // }
        ],
        msg_network_status: {
          uint32_bitrate: 0, // 网络码率 单位:bps
          uint32_rtt: 0, // 网络rtt
          uint32_lost: 0 // 网络总丢包率，服务端自己统计
        }
        // msg_qos: [
        //   {
        //     uint32_video_fps: 0, // 视频帧率
        //     uint32_video_width: 0, // 视频分辨率宽
        //     uint32_video_height: 0, // 视频分辨率高
        //     uint32_video_bitrate: 0, // 视频码率 单位:bps
        //     uint32_audio_bitrate: 0, // 音频码率 单位:bps
        //     uint32_video_stream_type: 0 // 视频流类型 2:大画面 3:小画面 7:辅路 (U/D)
        //   }
        // ]
      },
      msg_down_stream_info: [
        {
          msg_user_info: {
            str_identifier: '',
            uint64_tinyid: 0
          },
          msg_audio_status: {
            uint32_audio_cache_ms: 0,
            uint32_audio_format: 11, // 音频格式 11:opus 10:aac
            uint32_audio_sample_rate: 0, // 音频采样率
            uint32_audio_codec_bitrate: 0, // 音频编解码码率
            uint32_audio_total_bitrate: 0, // 音频码率包括原始包、FEC包、ARQ包
            uint32_audio_level: 0,
            uint32_audio_energy: 0,
            uint32_audio_receive: 0, // 音频应收包数
            uint32_audio_origin_lost: 0, // 音频原始丢包数
            uint32_audio_final_lost: 0 // 音频最终丢包数 服务端没有设置
          },
          msg_video_status: [
            {
              uint32_video_cache_ms: 0,
              uint32_video_stream_type: 0, // 视频流类型 2:大画面 3:小画面 7:辅路 (U/D)
              uint32_video_receive_fps: 0, // 视频接受帧率
              uint32_video_width: 0, // 视频分辨率宽
              uint32_video_height: 0, // 视频分辨率高
              uint32_video_codec_bitrate: 0, // 视频编解码码率
              uint32_video_receive: 0, // 视频应收包数
              uint32_video_origin_lost: 0, // 视频原始丢包数
              uint32_video_block_time: 0, // 视频2秒统计周期内的卡顿总时长(ms)， 可以获取
              uint32_video_dec_fps: 0, // 视频解码帧率
              uint32_video_codec_fps: 0 // 仪表盘视频渲染帧率，字段名是历史原因。
            }
          ],
          msg_network_status: {
            uint32_bitrate: 0, // 网络码率
            uint32_rtt: 0, // 网络rtt
            uint32_lost: 0 // 网络总丢包率，目前下行只上报丢包数，sdk可以计算，目前没有上报
          }
          // tcQos没有设置，未上报给流空
          // msg_qos: [
          //   {
          //     uint32_video_fps: 0, // 视频帧率
          //     uint32_video_width: 0, // 视频分辨率宽
          //     uint32_video_height: 0, // 视频分辨率高
          //     uint32_video_bitrate: 0, // 视频码率 单位:bps
          //     uint32_audio_bitrate: 0, // 音频码率 单位:bps
          //     uint32_video_stream_type: 0 // 视频流类型 2:大画面 3:小画面 7:辅路 (U/D)
          //   }
          // ]
        }
      ]
    };

    const stats = await this.getStats(uplinkConnection, downlinkConnections);

    if (JSON.stringify(this._prevReport) === '{}') {
      this._prevReport = JSON.parse(JSON.stringify(report));
    }
    this.prepareReport({ stats, report, freezeMap, uplinkConnection });
    this._prevReportTime = Date.now();
    return report;
  }

  getMediaPlayoutStats(stats: any[] | null) {
    let mediaPlayoutStats;
    if (!isArray(stats)) return;
    for (const item of stats) {
      if (item.type === 'media-playout') {
        const { synthesizedSamplesDuration, totalSamplesDuration } = item;
        mediaPlayoutStats = {
          synthesizedSamplesDuration,
          totalSamplesDuration
        };
        break;
      }
    }
    return mediaPlayoutStats;
  }
  // 采集未推流时, 更新视频/屏幕分享的采集帧率
  updateVideoCaptureStats(track: LocalVideoTrack | LocalScreenTrack | undefined, statTarget: { framesCaptured?: number; fpsCapture?: number }) {
    if (!track?.sourceTrack?.stats?.deliveredFrames) {
      return;
    }
    const { deliveredFrames } = track.sourceTrack.stats;
    statTarget.framesCaptured = deliveredFrames;
    const prevFrames = track.stat.framesCaptured;
    if (prevFrames && prevFrames > 0 && deliveredFrames >= prevFrames) {
      statTarget.fpsCapture = Math.floor((deliveredFrames - prevFrames) / this.statInterval);
    }
    track.stat.framesCaptured = deliveredFrames;
  }

  reset() {
    this._prevReportTime = 0;
    this._prevReport = {};

    this._prevEncoderImplementation = '';
    this._prevQualityLimitationReason = '';
    this._prevDecoderImplementationMap = new Map();
    [
      this.room.capturedLocalMainVideoTrack,
      this.room.capturedLocalAuxVideoTrack
    ].forEach(track => {
      if (track?.stat) (track.stat as LocalVideoStat).framesCaptured = 0;
    });
  }
}

interface StatsReport {
  msg_device_info: Partial<TC_DeviceInfo>,
  msg_up_stream_info: UplinkReport,
  msg_down_stream_info: DownlinkReport[];
}


interface SenderStat {
  audio: LocalAudioStat,
  video: LocalVideoStat,
  small: LocalVideoStat,
  auxiliary: LocalVideoStat,
  rtt: number
}

interface MediaPlayoutStats {
  synthesizedSamplesDuration: number,
  totalSamplesDuration: number,
}

interface ReceiverStat {
  tinyId: string,
  userId: string,
  rtt: number,
  hasAudio: boolean,
  hasVideo: boolean,
  hasAuxiliary: boolean,
  isSmallSubscribed: boolean,
  avSyncDelay: number,
  audio: {
    bytesReceived: number,
    packetsReceived: number,
    packetsLost: number,
    p2pDelay: number,
    totalJitter: number,
    totalJitterCount: number,
    audioLevel: number,
    totalAudioEnergy: number,
    insertedSamplesForDeceleration: number,
    removedSamplesForAcceleration: number,
    concealedSamples?: number,
    silentConcealedSamples?: number,
    totalSamplesReceived?: number,
    totalSamplesDuration?: number,
    estimatedPlayoutTimestamp?: number,
  },
  video: {
    bytesReceived: number,
    packetsReceived: number,
    packetsLost: number,
    framesReceived: number,
    framesDecoded: number,
    frameWidth: number,
    frameHeight: number,
    fpsDecoded: number,
    fpsRender?: number,
    freezeCount: number,
    totalFreezesDuration: number,
    totalJitter: number,
    totalJitterCount: number,
    p2pDelay: number,
    codec: string,
    estimatedPlayoutTimestamp?: number,
  },
  auxiliary: {
    bytesReceived: number,
    packetsReceived: number,
    packetsLost: number,
    framesReceived: number,
    framesDecoded: number,
    frameWidth: number,
    frameHeight: number,
    fpsDecoded: number,
    fpsRender?: number,
    totalJitter: number,
    totalJitterCount: number,
    p2pDelay: number,
    codec: string,
  }
}
